import { type useMutation } from '@apollo/client/react';

import type {
  UpdateBuyerLeadsStageMultipleMutation,
  UpdateBuyerLeadsStageMultipleMutationVariables,
} from '@graphql/__generated__/graphql';
import type { BuyerLeadKanbanStageOption } from '@modules/_shared/components/buyer-lead/buyerLeadUtils';
import { KabanMoveToColumnDialog } from '@modules/_shared/components/kanban/KabanMoveToColumnDialog';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { emitFeedRefresh } from '@modules/_shared/utils/feed-refresh';
import { useLocale } from '@shared/hooks/locale';

type UpdateMultipleBuyerLeadsFn = ReturnType<
  typeof useMutation<
    UpdateBuyerLeadsStageMultipleMutation,
    UpdateBuyerLeadsStageMultipleMutationVariables
  >
>[0];

type BuyerLeadMoveToColumnDialogProps = {
  columns: BuyerLeadKanbanStageOption[];
  selectedLeadIds: string[];
  opened: boolean;
  onClose: () => void;
  onReset: () => void;
  updateMultipleBuyerLeads: UpdateMultipleBuyerLeadsFn;
};

export const BuyerLeadMoveToColumnDialog: React.FC<
  BuyerLeadMoveToColumnDialogProps
> = ({
  columns,
  selectedLeadIds,
  opened,
  onClose,
  onReset,
  updateMultipleBuyerLeads,
}) => {
  const { t } = useLocale();
  const { me } = useAppData();

  return (
    <KabanMoveToColumnDialog
      columns={columns}
      onClose={onClose}
      onSubmit={async targetColumn => {
        if (me == null) {
          onReset();
          return;
        }

        const result = await updateMultipleBuyerLeads({
          variables: {
            buyerLeadIds: selectedLeadIds,
            stageId: targetColumn,
          },
        });
        emitFeedRefresh();
        onReset();

        return result;
      }}
      opened={opened}
      title={t('Move {{count}} leads to column', {
        count: selectedLeadIds.length,
      })}
    />
  );
};
